import { computed } from 'vue';
import moment from 'moment';
import { useI18n } from '@/composables/useI18n';
import { useStore } from '@/store';

export const usePeriodPickerRange = (format: string) => {
  const { t } = useI18n();
  const store = useStore();

  const baseItems = computed(() => [
    { value: 'today', text: t('Today') },
    { value: 'yesterday', text: t('Yesterday') },
    { value: 'last24hours', text: t('Last 24 Hours') },
    { value: 'last48hours', text: t('Last 48 Hours') },
    { value: 'last3days', text: t('Last 3 days') },
    { value: 'last7days', text: t('Last 7 days') },
    { value: 'last30days', text: t('Last 30 days') },
    { value: 'thisMonth', text: t('This Month') },
    { value: 'lastMonth', text: t('Last Month') },
    {
      value: 'maxAvailable',
      text: t('Max. available'),
      help: t(
        'Access the report data for the maximum available period for your plan or purchased add-on.',
      ),
    },
    { value: 'customDateTime', text: t('Date Range') },
  ]);

  const getRangeValueCollection = ({ timezone }: { timezone: string }) => {
    const resolvedTimezone = timezone || (store.user.defaultTimezone as string);

    return {
      today: {
        from: moment().startOf('day').format(format),
        to: moment().endOf('day').format(format),
      },
      yesterday: {
        from: moment().startOf('day').subtract(1, 'days').format(format),
        to: moment().endOf('day').subtract(1, 'days').format(format),
      },
      last3days: {
        from: moment().startOf('day').subtract(2, 'days').format(format),
        to: moment().endOf('day').format(format),
      },
      last7days: {
        from: moment().startOf('day').subtract(6, 'days').format(format),
        to: moment().endOf('day').format(format),
      },
      last30days: {
        from: moment().subtract(29, 'days').startOf('day').format(format),
        to: moment().endOf('day').format(format),
      },
      last24hours: {
        from: moment().tz(resolvedTimezone).startOf('hour').subtract(23, 'hours').format(format),
        to: moment().tz(resolvedTimezone).startOf('hour').add(1, 'hour').format(format),
      },
      last48hours: {
        from: moment().tz(resolvedTimezone).startOf('hour').subtract(47, 'hours').format(format),
        to: moment().tz(resolvedTimezone).startOf('hour').add(1, 'hour').format(format),
      },
      thisMonth: {
        from: moment().startOf('month').format(format),
        to: moment().endOf('day').format(format),
      },
      lastMonth: {
        from: moment().subtract(1, 'months').startOf('month').format(format),
        to: moment().subtract(1, 'months').endOf('month').format(format),
      },
      maxAvailable: {
        from: moment()
          .subtract(store.billing.limits?.dataRetention ?? 12, 'months')
          .startOf('day')
          .format(format),
        to: moment().endOf('day').format(format),
      },
    };
  };

  return {
    baseItems,
    getRangeValueCollection,
  };
};
